<template>
	<div>
		<slot name="over-table" />

		<div :class="`position-relative ${responsive ? 'table-responsive' : ''}`">
			<table :class="tableClasses">
				<thead>
					<tr>
						<th
							v-for="(name, index) in columnNames"
							@click="changeSort(rawColumnNames[index], index)"
							:class="['pr-2 align-middle', headerClass(index), sortingIconStyles(index), mobileClasses]"
							:style="headerStyles(index)"
							:key="index"
						>
							<slot :name="`${rawColumnNames[index]}-header`">
								<div class="d-inline">{{ name }}</div>
							</slot>
							<slot
								v-if="isSortable(index)"
								name="sorting-icon"
								:state="getIconState(index)"
								:classes="iconClasses(index)"
							>
								<FaIcon width="18" icon="arrow-up" :class="iconClasses(index)" />
							</slot>
						</th>
					</tr>

					<tr v-if="columnFilter" class="table-sm">
						<th v-for="(colName, index) in rawColumnNames" :class="headerClass(index)" :key="index">
							<slot :name="`${rawColumnNames[index]}-filter`">
								<input
									v-if="!fields || fields[index].filter !== false"
									class="w-100 table-filter"
									@input="columnFilterEvent(colName, $event.target.value, 'input')"
									@change="columnFilterEvent(colName, $event.target.value, 'change')"
									:value="columnFilterState[colName]"
								/>
							</slot>
						</th>
					</tr>
				</thead>

				<tbody :style="clickableRows ? 'cursor:pointer;' : null" class="position-relative">
					<tr
						v-for="(item, itemIndex) in currentItems"
						:class="item._classes"
						:tabindex="clickableRows ? 0 : null"
						@click="rowClicked(item, itemIndex + firstItemIndex)"
						:key="itemIndex"
					>
						<template v-for="(colName, index) in rawColumnNames">
							<slot
								v-if="$scopedSlots[colName]"
								:name="colName"
								:item="item"
								:index="itemIndex + firstItemIndex"
							/>
							<td v-else :class="cellClass(item, colName, index)" :key="index">{{ String(item[colName]) }}</td>
						</template>
					</tr>
					<tr
						v-if="$scopedSlots.details"
						class="p-0"
						style="border:none !important"
						:key="'details' + itemIndex"
					>
						<td :colspan="colspan" class="p-0" style="border:none !important">
							<slot name="details" :item="item" :index="itemIndex + firstItemIndex" />
						</td>
					</tr>
					<tr v-if="!currentItems.length">
						<td :colspan="colspan" class="no-data">
							<slot name="no-items-view">
								<div class="text-center my-5" v-if="passedItems.length">
									<h2>
										{{ $t('pagination.no_filtering_results') }}
										<FaIcon icon="ban" width="30" class="text-danger" />
									</h2>
								</div>
								<CImg
									v-else
									class="mx-auto d-block w-100 mb-3"
									style="max-width:200px;"
									src="//statics.affili.ir/storage/assets/empty-page.png"
								/>
							</slot>
						</td>
					</tr>
				</tbody>

				<tfoot v-if="footer && currentItems.length > 0" class="bg-gray-100 text-dark b-t-2">
					<tr>
						<th
							v-for="(name, index) in columnNames"
							@click="changeSort(rawColumnNames[index], index)"
							:class="['pr-2 align-middle', headerClass(index), sortingIconStyles(index), mobileClasses]"
							:style="headerStyles(index)"
							:key="index"
						>
							<slot :name="`${rawColumnNames[index]}-footer`">
								<div class="d-inline">{{ name }}</div>
							</slot>
							<slot v-if="isSortable(index)" name="sorting-icon" :state="getIconState(index)">
								<FaIcon width="18" icon="arrow-up" :class="iconClasses(index)" />
							</slot>
						</th>
					</tr>
				</tfoot>
				<slot name="caption" />
			</table>

			<slot name="loading" v-if="loading">
				<div
					style="position:absolute;left:0;top:0;bottom:0;right:0;background-color:rgb(255,255,255,0.4);"
				>
					<div style="position:absolute;bottom:50%;left:50%;transform:translateX(-50%);">
						<CSpinner color="success" />
					</div>
				</div>
			</slot>
		</div>

		<slot name="under-table" />

		<CPagination
			v-if="pagination.last_page > 1 && !pagination.no_paginate"
			:activePage.sync="pagination.current_page"
			:pages="pagination.last_page"
			:align="'center'"
			size="sm"
			:limit="pagination.limit || 5"
			@update:activePage="pagination.change"
		/>
	</div>
</template>

<script>
export default {
	name: 'CDataTable',
	props: {
		items: Array,
		fields: Array,
		itemsPerPage: {
			type: Number,
			default: 10
		},
		activePage: Number,
		pagination: [Boolean, Object],
		addTableClasses: [String, Array, Object],
		responsive: {
			type: Boolean,
			default: true
		},
		size: String,
		dark: Boolean,
		striped: Boolean,
		fixed: Boolean,
		hover: Boolean,
		border: Boolean,
		outlined: Boolean,
		itemsPerPageSelect: Boolean,
		sorter: [Boolean, Object],
		tableFilter: [Boolean, Object],
		columnFilter: [Boolean, Object],
		sorterValue: {
			type: Object,
			default: () => { return {} }
		},
		tableFilterValue: String,
		columnFilterValue: Object,
		footer: Boolean,
		loading: Boolean,
		clickableRows: Boolean
	},
	data() {
		return {
			tableFilterState: this.tableFilterValue,
			columnFilterState: {},
			sorterState: {
				column: null,
				asc: true
			},
			page: this.activePage || 1,
			perPageItems: this.pagination ? this.pagination.per_page : this.itemsPerPage,
			passedItems: this.items || []
		}
	},
	watch: {
		itemsPerPage(val) {
			this.perPageItems = val
		},
		sorterValue: {
			immediate: true,
			handler(val) {
				this.sorterState.column = val.column
				this.sorterState.asc = val.asc === false ? false : true
			}
		},
		tableFilterValue(val) {
			this.tableFilterState = val
		},
		columnFilterValue: {
			immediate: true,
			handler(val) {
				this.columnFilterState = Object.assign({}, val)
			}
		},
		items(val, oldVal) {
			if (
				val.length !== oldVal.length ||
				JSON.stringify(val) !== JSON.stringify(oldVal)
			) {
				this.passedItems = val
			}
		},
		totalPages: {
			immediate: true,
			handler(val) {
				this.$emit('pages-change', val)
			}
		}
	},
	computed: {
		mobileClasses() {
			if (this.$isMobile()) {
				return ['font-xs']
			}

			return []
		},
		columnFiltered() {
			let items = this.passedItems.slice()
			if (this.columnFilter && this.columnFilter.external) {
				return items
			}
			Object.entries(this.columnFilterState).forEach(([key, value]) => {
				const columnFilter = String(value).toLowerCase()
				if (columnFilter && this.rawColumnNames.includes(key)) {
					items = items.filter(item => {
						return String(item[key]).toLowerCase().includes(columnFilter)
					})
				}
			})
			return items
		},
		filterableCols() {
			return this.rawColumnNames.filter(name => {
				return this.generatedColumnNames.includes(name)
			})
		},
		tableFiltered() {
			let items = this.columnFiltered.slice()
			if (!this.tableFilterState || (this.tableFilter && this.tableFilter.external)) {
				return items
			}
			const filter = this.tableFilterState.toLowerCase()
			const hasFilter = (item) => String(item).toLowerCase().includes(filter)
			items = items.filter(item => {
				return this.filterableCols.filter(key => hasFilter(item[key])).length
			})
			return items
		},
		sortedItems() {
			const col = this.sorterState.column
			if (!col || !this.rawColumnNames.includes(col) || this.sorter.external) {
				return this.tableFiltered
			}
			//if values in column are to be sorted by numeric value they all have to be type number
			const flip = this.sorterState.asc ? 1 : -1
			return this.tableFiltered.slice().sort((a, b) => {
				return (a[col] > b[col]) ? 1 * flip : ((b[col] > a[col]) ? -1 * flip : 0)
			})
		},
		firstItemIndex() {
			return (this.computedPage - 1) * this.perPageItems || 0
		},
		paginatedItems() {
			return this.sortedItems.slice(
				(this.pagination ? 0 : this.firstItemIndex),
				this.firstItemIndex + this.perPageItems
			)
		},
		currentItems() {
			return this.computedPage ? this.paginatedItems : this.sortedItems
		},
		totalPages() {
			return Math.ceil((this.sortedItems.length) / this.perPageItems) || 1
		},
		computedPage() {
			return this.pagination ? this.pagination.current_page : this.activePage
		},
		generatedColumnNames() {
			return Object.keys(this.passedItems[0] || {}).filter(el => el.charAt(0) !== '_')
		},
		rawColumnNames() {
			if (this.fields) {
				return this.fields.map(el => el.key || el)
			}
			return this.generatedColumnNames
		},
		columnNames() {
			if (this.fields) {
				return this.fields.map(f => {
					return f.label !== undefined ? f.label : this.pretifyName(f.key || f)
				})
			}
			return this.rawColumnNames.map(el => this.pretifyName(el))
		},
		tableClasses() {
			return [
				'table',
				this.addTableClasses,
				{
					[`table-${this.size}`]: this.size,
					'table-dark': this.dark,
					'table-striped': this.striped,
					'b-table-fixed': this.fixed,
					'table-hover': this.hover,
					'table-bordered': this.border,
					'border': this.outlined
				}
			]
		},
		colspan() {
			return this.rawColumnNames.length
		},
		tableFilterData() {
			return {
				label: this.tableFilter.label || this.$t('table.filter'),
				placeholder: this.tableFilter.placeholder || this.$t('table.type_string')
			}
		}
	},
	methods: {
		changeSort(column, index) {
			if (!this.isSortable(index)) {
				return
			}
			//if column changed or sort was descending change asc to true
			const state = this.sorterState
			const columnRepeated = state.column === column
			if (!this.sorter || !this.sorter.resetable) {
				state.column = column
			} else {
				state.column = columnRepeated && state.asc === false ? null : column
			}
			state.asc = !(columnRepeated && state.asc)

			this.pagination.change(1, {
				sorter: {
					column: state.column,
					direction: state.asc ? 'asc' : 'desc',
				}
			})
		},
		columnFilterEvent(colName, value, type) {
			const isLazy = this.columnFilter && this.columnFilter.lazy === true
			if (isLazy && type === 'input' || !isLazy && type === 'change') {
				return
			}
			this.$set(this.columnFilterState, colName, value)
			this.$emit('update:column-filter-value', this.columnFilterState)
		},
		tableFilterChange(value, type) {
			const isLazy = this.tableFilter && this.tableFilter.lazy === true
			if (isLazy && type === 'input' || !isLazy && type === 'change') {
				return
			}
			this.tableFilterState = value
			this.$emit('update:table-filter-value', this.tableFilterState)
		},
		pretifyName(name) {
			return name.replace(/[-_.]/g, ' ')
				.replace(/ +/g, ' ')
				.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
				.split(' ')
				.map(word => word.charAt(0).toUpperCase() + word.slice(1))
				.join(' ')
		},
		cellClass(item, colName, index) {
			let classes = []
			if (item._cellClasses && item._cellClasses[colName]) {
				classes.push(item._cellClasses[colName])
			}
			if (this.fields && this.fields[index]._classes) {
				classes.push(this.fields[index]._classes)
			}
			return classes
		},
		isSortable(index) {
			return this.sorter && (!this.fields || this.fields[index].sorter !== false)
		},
		sortingIconStyles(index) {
			return { 'position-relative pr-4': this.sorter && this.isSortable(index) }
		},
		headerClass(index) {
			const fields = this.fields
			return fields && fields[index]._classes ? fields[index]._classes : ''
		},
		headerStyles(index) {
			let style = 'min-width:100px;'
			if (this.isSortable(index)) {
				style += `cursor:pointer;`
			}
			if (this.fields && this.fields[index] && this.fields[index]._style) {
				style += this.fields[index]._style
			}
			return style
		},
		rowClicked(item, index) {
			this.$emit('row-clicked', item, index)
		},
		getIconState(index) {
			const direction = this.sorterState.asc ? 'asc' : 'desc'
			return this.rawColumnNames[index] === this.sorterState.column ? direction : 0
		},
		iconClasses(index) {
			const state = this.getIconState(index)
			return [
				'icon-transition position-absolute arrow-position',
				{
					'transparent': !state,
					'rotate-icon': state === 'desc'
				}
			]
		},
		// paginationChange (e) {
		//   this.$emit('pagination-change', Number(e.target.value))
		//   this.perPageItems = Number(e.target.value)
		// }
	}
}
</script>

<style lang="scss" scoped>
.transparent {
	opacity: 0.4;
}
.icon-transition {
	-webkit-transition: transform 0.3s;
	transition: transform 0.3s;
}
.arrow-position {
	right: 0;
	top: 50%;
	-ms-transform: translateY(-50%);
	transform: translateY(-50%);
}
.rotate-icon {
	-ms-transform: translateY(-50%) rotate(-180deg);
	transform: translateY(-50%) rotate(-180deg);
}
@media (hover: hover), not all {
	.table-hover tbody tr:hover {
		background-color: rgba(#4a148c, 0.05);
	}
}
</style>
